import type { JSchema } from '../types';

export const parseJSONSchema = (schemaString: string): JSchema => {
  try {
    return JSON.parse(schemaString);
  } catch (error) {
    console.error('[validators]: failed to parse validation schema:', error);
    throw error;
  }
};

const normalizeFormatRef = (format?: string) => {
  // TODO: fix API, return proper format namespacing
  if (!format) {
    return;
  }

  const formatRef: Record<string, string> = {
    'x-date-ship-delivery-format': 'x-ref-format-date-equal-or-more',
    'x-ship-delivery-date-format': 'x-ref-format-date-equal-or-more',
  };
  if (!formatRef[format]) {
    return format;
  }
  return formatRef[format];
};

export const resolvePropertiesNames = <T extends JSchema>(schema: T, names: Record<string, string>): T => {
  if (!('properties' in schema && schema.properties)) {
    return schema;
  }

  return {
    ...schema,
    properties: Object.fromEntries(Object.entries(schema.properties).map(([key, value]) => [names[key] ?? key, value])),
    required: schema.required?.map((key) => names[key] ?? key),
  };
};

export const resolvePropertiesLabels = <T extends JSchema>(
  schema: T,
  labels: Record<string, string | Record<string, string>>,
  definitions?: Record<string, JSchema>,
): T => {
  const defs = definitions ?? ('definitions' in schema ? schema.definitions : undefined);

  if ('properties' in schema && schema.properties) {
    return {
      ...schema,
      properties: Object.fromEntries(
        Object.entries(schema.properties).map(([key, value]) => {
          const fieldLabel = labels[key];
          const newValue = resolvePropertiesLabels(value, typeof fieldLabel === 'object' ? fieldLabel : {}, defs);

          // TODO: refactor resolvePropertiesLabels
          const normalizedNewValue = {
            ...newValue,
            // @ts-expect-error
            ...(newValue.format ? { format: normalizeFormatRef(newValue.format) } : null),
          };

          if (typeof fieldLabel === 'string') {
            return [key, { ...normalizedNewValue, label: fieldLabel }];
          }

          return [key, normalizedNewValue];
        }),
      ),
    };
  }

  if ('items' in schema && schema.items) {
    const items = schema.items;

    if ('$ref' in items) {
      if (!defs) {
        return schema;
      }
      const refName = items.$ref.replace('#/definitions/', '');
      const refSchema = defs[refName];
      if (refSchema) {
        defs[refName] = resolvePropertiesLabels(refSchema, labels, defs);
      }
      return schema;
    }

    return {
      ...schema,
      items: resolvePropertiesLabels(items as JSchema, labels, defs),
    };
  }

  if ('definitions' in schema && schema.definitions) {
    return {
      ...schema,
      definitions: Object.fromEntries(
        Object.entries(schema.definitions).map(([key, value]) => [
          key,
          resolvePropertiesLabels(value, (labels[key] as Record<string, string>) ?? {}, defs),
        ]),
      ),
    };
  }

  return schema;
};
